import React, { createContext, useContext, useState, useEffect } from "react";

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [themeMode, setThemeMode] = useState(() => {
    // Get theme from localStorage or default to 'light'
    return localStorage.getItem("theme-mode") || "light";
  });

  const [effectiveTheme, setEffectiveTheme] = useState("light");

  // Detect system theme preference
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleSystemThemeChange = (e) => {
      if (themeMode === "system") {
        setEffectiveTheme(e.matches ? "dark" : "light");
      }
    };

    // Set initial effective theme
    if (themeMode === "system") {
      setEffectiveTheme(mediaQuery.matches ? "dark" : "light");
    } else {
      setEffectiveTheme(themeMode);
    }

    // Listen for system theme changes
    mediaQuery.addEventListener("change", handleSystemThemeChange);

    return () => {
      mediaQuery.removeEventListener("change", handleSystemThemeChange);
    };
  }, [themeMode]);

  // Apply theme class to document
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", effectiveTheme);
    document.documentElement.classList.remove("light-theme", "dark-theme");
    document.documentElement.classList.add(`${effectiveTheme}-theme`);
  }, [effectiveTheme]);

  // Save theme mode to localStorage
  useEffect(() => {
    localStorage.setItem("theme-mode", themeMode);
  }, [themeMode]);

  const setTheme = (mode) => {
    setThemeMode(mode);
  };

  const value = {
    themeMode, // 'light', 'dark', or 'system'
    effectiveTheme, // actual theme being applied ('light' or 'dark')
    setTheme,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export default ThemeContext;
